import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { Check, X } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'
import { DiscardConfirm, useDirtyClose } from './dialog'

/** Right-side drawer (dispatch). Base UI Dialog underneath, so focus is trapped and Esc closes. */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
  dirty,
  onDiscard,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  width?: 'md' | 'lg'
  /** #1233: something typed would be lost. Any close then asks "Discard changes?" first; the tab warns on unload. */
  dirty?: boolean
  /** Runs when the person chooses Discard, before `onOpenChange(false)`. */
  onDiscard?: () => void
}) {
  const guard = useDirtyClose({ open, dirty, onOpenChange, onDiscard })
  return (
    <DialogPrimitive.Root open={open} onOpenChange={guard.handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-40 bg-foreground/20 transition-opacity duration-(--duration-base) data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <DialogPrimitive.Popup
          data-slot="drawer-popup"
          onFocusCapture={guard.onFocusCapture}
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-hidden border-l border-border bg-background shadow-lg outline-none',
            'transition-transform duration-(--duration-slow) ease-(--ease-standard) data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full',
            width === 'md' ? 'sm:max-w-[640px]' : 'sm:max-w-[860px]',
          )}
        >
          <div data-slot="drawer-header" className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-6 py-4">
            <div>
              <DialogPrimitive.Title className="text-xl font-semibold">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">{description}</DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              data-slot="drawer-close"
              render={<Button variant="ghost" size="icon" aria-label="Close" title="Close (Esc)" />}
            >
              <X />
            </DialogPrimitive.Close>
          </div>
          <div data-slot="drawer-body" className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-6 py-5">
            {children}
          </div>
          {footer || guard.confirming ? (
            <div
              data-slot="drawer-footer"
              className="shrink-0 border-t border-border bg-canvas px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            >
              {guard.confirming ? <DiscardConfirm onDiscard={guard.discard} onKeepEditing={guard.keepEditing} /> : footer}
            </div>
          ) : null}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** Numbered steps for multi-step flows (dispatch: Need · Who · Packet · Book). */
export function Stepper({ steps, current, onStep }: { steps: string[]; current: number; onStep?: (index: number) => void }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Progress">
      {steps.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo'
        return (
          <li key={label} className="flex flex-1 items-center gap-2 last:flex-none">
            <button
              type="button"
              onClick={() => onStep?.(i)}
              aria-current={state === 'current' ? 'step' : undefined}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <span
                className={cn(
                  'inline-flex size-7 items-center justify-center rounded-full border text-sm font-semibold',
                  state === 'current' && 'border-primary bg-primary text-primary-foreground',
                  state === 'done' && 'border-primary bg-primary-soft text-primary',
                  state === 'todo' && 'border-border-strong text-muted-foreground',
                )}
              >
                {state === 'done' ? <Check className="size-4" /> : i + 1}
              </span>
              <span className={cn(state === 'current' ? 'font-semibold text-primary' : 'text-muted-foreground')}>{label}</span>
            </button>
            {i < steps.length - 1 ? <span aria-hidden className="h-px flex-1 bg-border-strong" /> : null}
          </li>
        )
      })}
    </ol>
  )
}
